// 동일 도메인 탭이 너무 많이 열리는 것을 방지
// - localStorage에 열린 탭 목록 저장
// - BroadcastChannel로 탭 간 통신
// - 3개 초과 시 가장 오래된 탭을 /tab-closed 페이지로 교체
//
// QR 스캐너가 연 탭은 window.close() 불가 (브라우저 보안 정책) → replace 방식 사용

function safeStorage() {
  try {
    if (typeof window === 'undefined') return null;
    const k = '__tab_mgr_test__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return window.localStorage;
  } catch {
    return null;
  }
}

function loadTabs(storage, key) {
  try {
    const raw = storage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveTabs(storage, key, tabs) {
  try {
    storage.setItem(key, JSON.stringify(tabs));
  } catch {}
}

/**
 * 탭 등록 — 동일 도메인에서 maxTabs(기본 3)개 초과 시 가장 오래된 탭을 교체
 * @param {{channelName: string, storageKey?: string, maxTabs?: number, closedUrl?: string, staleMs?: number}} opts
 * @returns {() => void} 언등록 함수 (React cleanup용)
 */
export function registerTab(opts) {
  if (typeof window === 'undefined') return () => {};

  const storage = safeStorage();
  if (!storage) return () => {};

  const storageKey = opts.storageKey || 'open_tabs';
  const maxTabs = opts.maxTabs ?? 3;
  const closedUrl = opts.closedUrl || '/tab-closed?reason=auto';
  const staleMs = opts.staleMs ?? 24 * 3600 * 1000;

  // /tab-closed 페이지 자체에서는 등록 스킵 (재귀 방지)
  if (window.location.pathname.startsWith('/tab-closed')) {
    return () => {};
  }

  const myId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const openedAt = Date.now();

  const current = loadTabs(storage, storageKey).filter(
    (t) => Date.now() - t.openedAt < staleMs,
  );
  current.push({ id: myId, openedAt });
  saveTabs(storage, storageKey, current);

  let bc = null;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      bc = new BroadcastChannel(opts.channelName);
    }
  } catch {}

  const handleMessage = (ev) => {
    const data = ev.data;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'tab-close' && data.targetId === myId) {
      try {
        window.location.replace(closedUrl);
      } catch {}
    }
  };
  if (bc) bc.onmessage = handleMessage;

  if (current.length > maxTabs) {
    const sorted = [...current].sort((a, b) => a.openedAt - b.openedAt);
    const victims = sorted.slice(0, current.length - maxTabs).filter((t) => t.id !== myId);
    victims.forEach((v) => {
      try {
        bc?.postMessage({ type: 'tab-close', targetId: v.id });
      } catch {}
    });
  }

  const cleanup = () => {
    try {
      const list = loadTabs(storage, storageKey).filter((t) => t.id !== myId);
      saveTabs(storage, storageKey, list);
    } catch {}
    try {
      bc?.close();
    } catch {}
  };

  const onUnload = () => cleanup();
  window.addEventListener('pagehide', onUnload);
  window.addEventListener('beforeunload', onUnload);

  return () => {
    window.removeEventListener('pagehide', onUnload);
    window.removeEventListener('beforeunload', onUnload);
    cleanup();
  };
}

// ===== 하위 호환 레이어 =====
// 기존 CaricatureGenerator.jsx는 tabManager.registerNewTab(qr) / notifyQRChange(qr) / cleanup() 사용
// 새 설계로 전환하면서 기존 API는 no-op으로 남겨둠 (App.jsx에서 registerTab()이 전역 처리)
const tabManager = {
  registerNewTab: (_qrCode) => {
    // 전역 registerTab()으로 대체됨
  },
  notifyQRChange: (_qrCode) => {
    // 전역 registerTab()으로 대체됨
  },
  cleanup: () => {
    // 전역 registerTab()이 cleanup 처리
  },
};

export default tabManager;
